import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Music2, Store } from 'lucide-react';
import { getSite } from '@/lib/site';

const SOCIAL_META = [
  ['facebook', Facebook, 'Facebook'],
  ['instagram', Instagram, 'Instagram'],
  ['tiktok', Music2, 'TikTok'],
  ['youtube', Youtube, 'YouTube'],
  ['youtube_store', Store, 'YouTube Store'],
];

const CITIES = ['West Palm Beach', 'Palm Beach Gardens', 'Wellington', 'Jupiter', 'Juno Beach', 'Lake Worth', 'Boca Raton', 'Delray Beach', 'Boynton Beach', 'Fort Lauderdale', 'Miami'];
const SERVICE_LINKS = [
  { to: '/services/handyman', label: 'Handyman' },
  { to: '/services/painting', label: 'Painting' },
  { to: '/services/flooring', label: 'Flooring' },
  { to: '/services/kitchen', label: 'Kitchen' },
  { to: '/services/bathroom', label: 'Bathroom' },
  { to: '/services/landscaping', label: 'Landscaping' },
  { to: '/services/pressure-washing', label: 'Pressure Washing' },
  { to: '/services/cleaning', label: 'Cleaning' },
];

export default function Footer() {
  const [social, setSocial] = useState(null);
  useEffect(() => {
    let alive = true;
    getSite().then(site => { if (alive) setSocial(site?.social || null); });
    return () => { alive = false; };
  }, []);
  const socialLinks = SOCIAL_META.filter(([key]) => social?.[key]);

  return (
    <footer className="mt-20 border-t border-[#E6E9EE] bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-4">
          <img src="/logo.png" alt="Royal Palm Services" className="h-32 w-auto object-contain -ml-1" />
          <p className="mt-3 text-sm text-slate-600 max-w-sm">South Florida's trusted home & commercial service professionals. Fully insured, and obsessed with craft.</p>
          <div className="mt-5 space-y-2 text-sm text-slate-700">
            <a href="tel:5618848900" data-testid="footer-call-now-button" className="flex items-center gap-2 hover:text-[#17352D]"><Phone className="h-4 w-4 text-[#C8922F]" /> 561-884-8900</a>
            <a href="mailto:support@Royalpalmservice.com" data-testid="footer-email-link" className="flex items-center gap-2 hover:text-[#17352D]"><Mail className="h-4 w-4 text-[#C8922F]" /> support@Royalpalmservice.com</a>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#C8922F] mt-0.5" /> Serving Palm Beach, Broward, Martin, St. Lucie & Miami-Dade</div>
          </div>
          {socialLinks.length > 0 && (
            <div className="mt-5 flex items-center gap-2" data-testid="footer-social-links">
              {socialLinks.map(([key, Icon, label]) => (
                <a
                  key={key}
                  href={social[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  data-testid={`footer-social-${key}`}
                  className="h-9 w-9 rounded-full border border-[#E6E9EE] flex items-center justify-center text-slate-500 hover:text-white hover:bg-[#17352D] hover:border-[#17352D] transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Services</div>
          <ul className="space-y-2 text-sm">
            {SERVICE_LINKS.map((s) => (
              <li key={s.to}><Link to={s.to} className="text-slate-700 hover:text-[#17352D]">{s.label}</Link></li>
            ))}
            <li><Link to="/services" className="text-[#17352D] font-semibold hover:underline">All Services →</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-slate-700 hover:text-[#17352D]">About</Link></li>
            <li><Link to="/gallery" className="text-slate-700 hover:text-[#17352D]">Project Gallery</Link></li>
            <li><Link to="/reviews" className="text-slate-700 hover:text-[#17352D]">Reviews</Link></li>
            <li><Link to="/financing" className="text-slate-700 hover:text-[#17352D]">Financing</Link></li>
            <li><Link to="/careers" className="text-slate-700 hover:text-[#17352D]">Careers</Link></li>
            <li><Link to="/contact" className="text-slate-700 hover:text-[#17352D]">Contact</Link></li>
            <li><Link to="/returns" data-testid="footer-returns-link" className="text-slate-700 hover:text-[#17352D]">Return & Refund Policy</Link></li>
            <li><Link to="/track" data-testid="footer-track-link" className="text-slate-700 hover:text-[#17352D]">Track Your Order</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Areas Served</div>
          <ul className="space-y-1 text-sm text-slate-600">
            {CITIES.slice(0, 8).map((c) => <li key={c}>{c}, FL</li>)}
          </ul>
        </div>
      </div>

      <div className="border-t border-[#E6E9EE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} Royal Palm Services. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/returns" className="hover:text-[#17352D]">Returns & Refunds</Link>
            <span>Fully Insured</span>
            <span>BBB A+ Accredited</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
